import { removeMiniIcon } from '@/shared/ui/assets';
import { ErrorMessage } from '@/shared/ui/components';
import classNames from 'classnames';
import { forwardRef, memo, useId, useMemo } from 'react';
import { ReactMultiEmail, isEmail } from 'react-multi-email';
import { useMultiEmailInput } from './useMultiEmailInput';
import type { MultiEmailInputProps } from './multiEmailInput.types';
import './styles.scss';

/**
 * Renders the RMA multi-email field with its title, optional subtitle and error message, wrapping ReactMultiEmail with isEmail validation and removable email tags. Memoises the input on the email list so the tags are not rebuilt on every keystroke, and forwards email-list and input-value changes to useMultiEmailInput.
 *
 * @returns The multi-email input block.
 */
const MultiEmailInput = forwardRef((props: MultiEmailInputProps, ref) => {
  const { state, onChangeEmails, onChangeInputValue } = useMultiEmailInput(props, ref);
  const inputId = useId();

  /**
   * Renders one entered address as a removable tag inside the multi-email input. Shows the address next to a handle whose click calls removeEmail with the tag's index.
   *
   * @param email - Address to display in the tag.
   * @param index - Position of the tag, used as its key and remove target.
   * @param removeEmail - Callback removing the address at a given index.
   * @returns The tag element for the address.
   */
  const getEmailLabel = (email: any, index: any, removeEmail: any) => (
    <div data-tag key={index}>
      {email}
      <span data-tag-handle onClick={() => removeEmail(index)}>
        <img src={removeMiniIcon} alt="remove" />
      </span>
    </div>
  );

  /**
   * The memoized ReactMultiEmail element for the input. Bound to the hook's ref, email list and change handler with isEmail validation and the tag renderer, and rebuilt only when the email list changes.
   *
   * @returns The ReactMultiEmail element.
   */
  const inputJSX = useMemo(
    () => (
      <ReactMultiEmail
        id={inputId}
        className={classNames('flex-1 custom-email-input', props.isError && 'error')}
        emails={state.emailList}
        onChange={onChangeEmails}
        onChangeInput={onChangeInputValue}
        placeholder="Enter emails, comma or space separated"
        validateEmail={(email) => isEmail(email)} // *: Return boolean
        getLabel={getEmailLabel}
      />
    ),
    [state.emailList],
  );

  return (
    <div className="multi-emails-input-v2">
      <label htmlFor={inputId} className="title">
        {props.title} {props.subtitle && <span className="sub-title">{props.subtitle}</span>}
      </label>

      {inputJSX}

      {props.isError && !!props.errorMessage && (
        <ErrorMessage className="error-message" errorMessage={props.errorMessage} hasIcon />
      )}
    </div>
  );
});

(MultiEmailInput as any).defaultProps = {
  isError: false,
  errorMessage: '',
  title: '',
  subtitle: '',
};

export default memo(MultiEmailInput);
